import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { pb, appMode } from "@/lib/pocketbase";
import { useToast } from "@/context/ToastContext";
import { itemsById, prereqOf } from "@/lib/deps";
import type { Project, Phase, Item, Subtask, Note, Contact, Artifact, Folder, ScopeChange } from "@/lib/types";
import { closeTodoistTask, reopenTodoistTask } from "@/lib/todoistClient";
import { applyInheritedClients } from "@/lib/inheritClients";

// --- Projects ---------------------------------------------------------------

export function useProjects() {
  return useQuery({
    queryKey: ["projects"],
    queryFn: () => pb.collection("projects").getFullList<Project>({ sort: "name" }),
  });
}

export function useProject(id?: string) {
  return useQuery({
    enabled: !!id,
    queryKey: ["project", id],
    queryFn: () => pb.collection("projects").getOne<Project>(id!),
  });
}

/**
 * The unified Inbox: a real project (kind === "inbox") with one phase, so
 * captured tasks are ordinary `items` that show up across every view. Ensures
 * the project + phase exist, creating them once if needed. Matches on `kind`
 * client-side so it works identically against live PB and the demo mock.
 */
export function useInboxProject() {
  return useQuery({
    queryKey: ["inbox-project"],
    staleTime: Infinity,
    queryFn: async () => {
      const userId = (pb.authStore.model as any)?.id;
      const projects = await pb.collection("projects").getFullList<Project>({ sort: "created" });
      // Prefer Todoist's own Inbox (tagged kind === "inbox" by the sync). Only
      // fall back to creating a local one when there is no mirror yet — in demo
      // mode, or before the first sync.
      let inbox = projects.find((p) => p.kind === "inbox" && p.todoistId)
        ?? projects.find((p) => p.kind === "inbox");
      if (!inbox) {
        inbox = await pb.collection("projects").create<Project>({
          user: userId, name: "Inbox", client: "", description: "",
          color: "#4A4E69", status: "active", kind: "inbox", taskListName: "Inbox", scope: "",
        } as any);
      }
      const phases = await pb.collection("phases").getFullList<Phase>({ filter: `project = "${inbox.id}"`, sort: "position" });
      let phaseId = phases[0]?.id;
      if (!phaseId) {
        const ph = await pb.collection("phases").create<Phase>({ project: inbox.id, name: "Inbox", color: "#4A4E69", position: 0 } as any);
        phaseId = ph.id;
      }
      return { projectId: inbox.id, phaseId };
    },
  });
}

/**
 * Gives sub-projects with no client their parent's (lib/inheritClients), then
 * refreshes. Call after linking a project to a client.
 */
export function useInheritClients() {
  const qc = useQueryClient();
  return useCallback(async () => {
    // A failure here must not turn the save it follows into "Save failed";
    // the next Todoist sync fills in whatever was missed.
    const filled = await applyInheritedClients().catch(() => 0);
    if (filled) {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project"] });
    }
    return filled;
  }, [qc]);
}

export function useSaveProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Project> & { id?: string }) => {
      const { id, ...data } = patch;
      if (id) return pb.collection("projects").update<Project>(id, data);
      // `user` is a required relation; stamp it from the auth store on create.
      const userId = (pb.authStore.model as any)?.id;
      return pb.collection("projects").create<Project>({ ...data, user: userId });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project"] });
    },
  });
}

/**
 * Delete a project. Phases and items cascade at the database level, so this
 * removes the whole tree of work under it — the caller is responsible for
 * confirming that first.
 */
export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pb.collection("projects").delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["phases"] });
      qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

// --- Phases -----------------------------------------------------------------

export function usePhases(projectId?: string) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ["phases", projectId],
    queryFn: () =>
      pb.collection("phases").getFullList<Phase>({
        filter: `project = "${projectId}"`,
        sort: "position",
      }),
  });
}

// All phases across every project — read-only, mirrors useAllItems. The
// cross-project Roadmap Gantt groups items by phase and needs phase records
// (name, color, dates) it can't get from items alone.
export function useAllPhases() {
  return useQuery({
    queryKey: ["phases", "all"],
    queryFn: () =>
      pb.collection("phases").getFullList<Phase>({ sort: "position" }),
  });
}

// --- Items ------------------------------------------------------------------

export function useAllItems() {
  return useQuery({
    queryKey: ["items", "all"],
    queryFn: () =>
      pb.collection("items").getFullList<Item>({
        sort: "due",
        expand: "phase,phase.project",
      }),
  });
}

export function useItemsForProject(projectId?: string) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ["items", "project", projectId],
    queryFn: () =>
      pb.collection("items").getFullList<Item>({
        filter: `phase.project = "${projectId}"`,
        sort: "position",
        expand: "phase",
      }),
  });
}

// --- Subtasks ---------------------------------------------------------------

export function useAllSubtasks() {
  return useQuery({
    queryKey: ["subtasks", "all"],
    queryFn: () => pb.collection("subtasks").getFullList<Subtask>({ sort: "position" }),
  });
}

export function useSubtasks(itemId?: string) {
  return useQuery({
    enabled: !!itemId,
    queryKey: ["subtasks", "item", itemId],
    queryFn: () =>
      pb.collection("subtasks").getFullList<Subtask>({
        filter: `item = "${itemId}"`,
        sort: "position",
      }),
  });
}

/** Map of itemId -> { done, total } for "2/3" progress badges. */
export function subtaskCounts(subs: Subtask[]): Map<string, { done: number; total: number }> {
  const m = new Map<string, { done: number; total: number }>();
  for (const s of subs) {
    const c = m.get(s.item) || { done: 0, total: 0 };
    c.total += 1;
    if (s.done) c.done += 1;
    m.set(s.item, c);
  }
  return m;
}

export function useSubtaskMutations(itemId?: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["subtasks"] });
  return {
    add: useMutation({
      mutationFn: (title: string) =>
        pb.collection("subtasks").create<Subtask>({ item: itemId, title, done: false, position: Date.now() }),
      onSuccess: invalidate,
    }),
    toggle: useMutation({
      mutationFn: (s: Subtask) => pb.collection("subtasks").update<Subtask>(s.id, { done: !s.done }),
      onSuccess: invalidate,
    }),
    update: useMutation({
      // Accepts a partial patch so a checklist item's title and/or note can be
      // edited independently.
      mutationFn: ({ id, ...patch }: { id: string; title?: string; note?: string }) =>
        pb.collection("subtasks").update<Subtask>(id, patch),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => pb.collection("subtasks").delete(id),
      onSuccess: invalidate,
    }),
  };
}

// --- Mutations --------------------------------------------------------------

/**
 * Dependency-aware completion toggle. Refuses to complete an item whose
 * prerequisite isn't done yet and explains why via a toast. Reads the cached
 * full item list to resolve the prerequisite, so it works from any view.
 */
export function useToggleItem() {
  const qc = useQueryClient();
  const toast = useToast();
  return useCallback(
    async (item: Item) => {
      if (item.status !== "done") {
        const all = qc.getQueryData<Item[]>(["items", "all"]) ?? [];
        const prereq = prereqOf(item, itemsById(all));
        if (prereq && prereq.status !== "done") {
          toast(`Waiting on "${prereq.title}" — finish it first`);
          return;
        }
      }
      const previous = item.status;
      const next = previous === "done" ? "todo" : "done";

      // Todoist first, PocketBase second. The mirror is a projection: if the
      // write-back fails and we had already flipped the local row, the next
      // sync would silently revert it and the completion would just vanish.
      // Failing here instead leaves both sides agreeing that it is not done.
      if (item.todoistId) {
        try {
          await (next === "done" ? closeTodoistTask(item.todoistId) : reopenTodoistTask(item.todoistId));
        } catch (err) {
          toast(`Could not update Todoist — ${err instanceof Error ? err.message : "unknown error"}`);
          return;
        }
      }

      await pb.collection("items").update<Item>(item.id, { status: next });
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["projects"] });

      // Undo is just the old status back — every caller of toggle gets this,
      // so the checkbox, the drawer, the Grid and the swipe all behave alike.
      toast(next === "done" ? `Completed "${item.title}"` : `Reopened "${item.title}"`, {
        undo: async () => {
          // Undo has to reach Todoist too, or the next sync re-applies the
          // completion the user just took back.
          if (item.todoistId) {
            try {
              await (previous === "done" ? closeTodoistTask(item.todoistId) : reopenTodoistTask(item.todoistId));
            } catch {
              toast("Undo failed in Todoist — the task is still marked there");
              return;
            }
          }
          await pb.collection("items").update<Item>(item.id, { status: previous });
          qc.invalidateQueries({ queryKey: ["items"] });
          qc.invalidateQueries({ queryKey: ["projects"] });
        },
      });
    },
    [qc, toast]
  );
}

export function useUpdateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Item> & { id: string }) =>
      pb.collection("items").update<Item>(patch.id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items"] }),
  });
}

// --- Phase mutations --------------------------------------------------------

export function useSavePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Phase> & { id?: string }) => {
      const { id, ...data } = patch;
      return id
        ? pb.collection("phases").update<Phase>(id, data)
        : pb.collection("phases").create<Phase>(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["phases"] }),
  });
}

/**
 * Delete a phase, moving its items to another phase rather than deleting them
 * (§8.2). Deleting a phase used to delete every item inside it, which is a
 * data-loss trap sitting behind a single menu click.
 */
export function useDeletePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ phaseId, moveToPhaseId }: { phaseId: string; moveToPhaseId: string }) => {
      const items = await pb.collection("items").getFullList<Item>({ filter: `phase = "${phaseId}"` });
      for (const it of items) await pb.collection("items").update(it.id, { phase: moveToPhaseId });
      await pb.collection("phases").delete(phaseId);
      return items.length;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["phases"] });
      qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

/** Duplicate a phase together with all of its items. */
export function useDuplicatePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (phase: Phase) => {
      const items = await pb.collection("items").getFullList<Item>({ filter: `phase = "${phase.id}"`, sort: "position" });
      const copy = await pb.collection("phases").create<Phase>({
        project: phase.project, name: phase.name + " (copy)", color: phase.color,
        start: phase.start || null, end: phase.end || null, position: (phase.position ?? 0) + 1,
      } as any);
      for (const it of items) {
        await pb.collection("items").create({
          phase: copy.id, title: it.title, type: it.type, status: it.status, priority: it.priority,
          start: it.start || null, due: it.due || null, notes: it.notes, position: it.position,
          syncEnabled: false, clientVisible: it.clientVisible, recur: it.recur,
        } as any);
      }
      return copy;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["phases"] });
      qc.invalidateQueries({ queryKey: ["items"] });
    },
  });
}

/** Set an item's status directly (used by kanban drag-and-drop). */
export function useSetItemStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: Item["status"] }) =>
      pb.collection("items").update<Item>(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

// --- Notes ------------------------------------------------------------------

export function useNotes(projectId?: string) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ["notes", projectId],
    queryFn: () => pb.collection("notes").getFullList<Note>({ filter: `project = "${projectId}"`, sort: "-pinned,-created" }),
  });
}

export function useNoteMutations(projectId?: string) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["notes"] });
  return {
    add: useMutation({ mutationFn: (body: string) => pb.collection("notes").create<Note>({ project: projectId, body, pinned: false } as any), onSuccess: inv }),
    update: useMutation({ mutationFn: (n: Partial<Note> & { id: string }) => pb.collection("notes").update<Note>(n.id, n), onSuccess: inv }),
    remove: useMutation({ mutationFn: (id: string) => pb.collection("notes").delete(id), onSuccess: inv }),
  };
}

// --- Contacts & artifacts (About tab) ---------------------------------------

export function useContacts(projectId?: string) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ["contacts", projectId],
    queryFn: () => pb.collection("contacts").getFullList<Contact>({ filter: `project = "${projectId}"`, sort: "position" }),
  });
}

export function useContactMutations(projectId?: string) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["contacts"] });
  return {
    add: useMutation({
      mutationFn: (position: number) =>
        pb.collection("contacts").create<Contact>({ project: projectId, name: "", role: "", email: "", position } as any),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (c: Partial<Contact> & { id: string }) => pb.collection("contacts").update<Contact>(c.id, c),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => pb.collection("contacts").delete(id), onSuccess: inv }),
  };
}

// --- Scope changes (§13) ----------------------------------------------------
// Newest first: a change log is read from the top.

export function useScopeChanges(projectId?: string) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ["scope_changes", projectId],
    queryFn: () =>
      pb.collection("scope_changes").getFullList<ScopeChange>({
        filter: 'project = "' + projectId + '"',
        sort: "-date",
      }),
  });
}

export function useScopeChangeMutations(projectId?: string) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["scope_changes"] });
  return {
    add: useMutation({
      mutationFn: (date: string) =>
        pb.collection("scope_changes").create<ScopeChange>({ project: projectId, date, body: "", impact: "" } as any),
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (c: Partial<ScopeChange> & { id: string }) => pb.collection("scope_changes").update<ScopeChange>(c.id, c),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => pb.collection("scope_changes").delete(id), onSuccess: inv }),
  };
}

export function useArtifacts(projectId?: string) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ["artifacts", projectId],
    queryFn: () => pb.collection("artifacts").getFullList<Artifact>({ filter: `project = "${projectId}"`, sort: "position" }),
  });
}

// Read a File as a data URL, for inline storage in the demo (no file backend).
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/**
 * Build a download URL for an artifact. Uploaded files resolve differently by
 * mode: LIVE uses the PocketBase file URL (routed through the /api proxy), DEMO
 * uses the inline data URL. Linked artifacts fall back to driveUrl.
 */
export function artifactHref(a: Artifact): string | undefined {
  if (appMode === "live" && a.file) {
    try { return (pb as any).files.getUrl(a, a.file); } catch { return undefined; }
  }
  if (a.fileData) return a.fileData;
  return a.driveUrl || undefined;
}

export function useArtifactMutations(projectId?: string) {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: ["artifacts"] });
  return {
    add: useMutation({
      mutationFn: ({ label, kind, driveUrl, position }: { label: string; kind: string; driveUrl: string; position: number }) =>
        pb.collection("artifacts").create<Artifact>({ project: projectId, label, kind, driveUrl, position } as any),
      onSuccess: inv,
    }),
    // Upload a stored file. LIVE posts multipart/form-data to the PocketBase
    // `file` field; DEMO stores the bytes inline as a data URL so the mock
    // (localStorage) can round-trip it. Keep demo uploads small.
    upload: useMutation({
      mutationFn: async ({ label, kind, file, position }: { label: string; kind: string; file: File; position: number }) => {
        if (appMode === "live") {
          const fd = new FormData();
          fd.append("project", projectId || "");
          fd.append("label", label);
          fd.append("kind", kind);
          fd.append("position", String(position));
          fd.append("file", file);
          return pb.collection("artifacts").create<Artifact>(fd as any);
        }
        const fileData = await fileToDataUrl(file);
        return pb.collection("artifacts").create<Artifact>({
          project: projectId, label, kind, fileName: file.name, fileData, position,
        } as any);
      },
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: (a: { id: string; label: string }) => pb.collection("artifacts").update<Artifact>(a.id, { label: a.label }),
      onSuccess: inv,
    }),
    remove: useMutation({
      mutationFn: (id: string) => pb.collection("artifacts").delete(id),
      onSuccess: inv,
    }),
  };
}

// --- Folders ----------------------------------------------------------------

export function useFolders() {
  return useQuery({
    queryKey: ["folders"],
    queryFn: () => pb.collection("folders").getFullList<Folder>({ sort: "position,name" }),
  });
}

export function useFolderMutations() {
  const qc = useQueryClient();
  const inv = () => {
    qc.invalidateQueries({ queryKey: ["folders"] });
    qc.invalidateQueries({ queryKey: ["projects"] });
  };
  return {
    add: useMutation({
      mutationFn: ({ name, color, position }: { name: string; color: string; position: number }) => {
        const userId = (pb.authStore.model as any)?.id;
        return pb.collection("folders").create<Folder>({ user: userId, name, color, position } as any);
      },
      onSuccess: inv,
    }),
    update: useMutation({
      mutationFn: ({ id, ...patch }: { id: string; name?: string; color?: string; position?: number }) =>
        pb.collection("folders").update<Folder>(id, patch),
      onSuccess: inv,
    }),
    /**
     * Put the folders in this order: renumbered 0..n, writing only those
     * whose position actually changes. The sidebar and the Projects page both
     * list folders by position, so one move shows in both.
     */
    reorder: useMutation({
      mutationFn: async (ordered: Folder[]) => {
        for (let i = 0; i < ordered.length; i++) {
          if (ordered[i].position !== i) await pb.collection("folders").update(ordered[i].id, { position: i } as any);
        }
      },
      onSuccess: inv,
    }),
    // Deleting a folder re-files its projects to "Unfiled" (clears folderId)
    // rather than cascading the delete to the projects.
    remove: useMutation({
      mutationFn: async (id: string) => {
        const members = await pb.collection("projects").getFullList<Project>({ filter: `folderId = "${id}"` });
        for (const p of members) await pb.collection("projects").update(p.id, { folderId: null } as any);
        await pb.collection("folders").delete(id);
      },
      onSuccess: inv,
    }),
  };
}
